import { useMemo, useState } from "react";
import {
  Box,
  Input,
  MenuCheckboxItem,
  MenuGroup,
  MenuGroupLabel,
  Text,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import {
  groupQuickOptions,
  matchesQuery,
  type QuickFilterOption,
  SEARCH_THRESHOLD,
} from "./quick-filter-options";
import { QuickOptionFace } from "./quick-option-face";


/** A pool opens at a readable height and scrolls past it. Uncapped, a
 *  200-member roster runs the full viewport: the menu the submenu hangs off and
 *  the field that searches the roster end up at opposite ends of the screen,
 *  and the pointer has to cross the whole list to get back to the row. */
const POOL_MAX_HEIGHT = 320;

type QuickOptionSubmenuProps = Omittable<{
  options: QuickFilterOption[];
  value: unknown;
  onChange: (value: unknown) => void;
  /** The filter's name, for the search field's accessible name. The row this
   *  submenu hangs off already says it on screen. */
  label: string;
}>;


/**
 * ONE quick filter's values, as the submenu its row opens on hover.
 *
 * Every pool reaches the reader through this one submenu — a member roster, the
 * follow-up states, a workflow and its stages — so the menu's own list holds no
 * roster. The search appears only once the set outgrows a glance; four named
 * states get no field to skip past. A filter with a chip of its own on the
 * toolbar (`AssigneeFilterChip`) opens the same list as its root popup, so the
 * two doors show one pool.
 *
 * `MenuCheckboxItem` does not close its menu on press, so picking three
 * assignees is one visit; the check IS the applied state, and unchecking is how
 * one value comes off.
 */
export function QuickOptionSubmenu({
  options,
  value,
  onChange,
  label,
}: QuickOptionSubmenuProps) {
  const [query, setQuery] = useState("");
  const trimmed = query.trim().toLowerCase();
  const searchable = options.length > SEARCH_THRESHOLD;

  const groups = useMemo(
    () =>
      groupQuickOptions(
        trimmed ? options.filter((o) => matchesQuery(o, trimmed)) : options,
      ),
    [options, trimmed],
  );

  return (
    <>
      {searchable ? (
        // The menu's keydown handler is released to this field by the popup's
        // `releaseKeyboardToTextField` — without it Base UI's typeahead eats
        // every printable key before the input sees it.
        // `sm` below: the same lead-in the Simple menu gives its own search,
        // so the first row is not pressed against the field.
        <Box paddingX="2xs" paddingBottom="sm">
          <Input
            aria-label={`Search ${label}`}
            size="sm"
            placeholder="Search…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </Box>
      ) : null}
      {/* Only the pool scrolls: the search band sits above this box and stays
          put, where a cap on the popup itself would carry the field off the top
          with the list. */}
      <Box direction="column" overflowY="auto" maxHeight={POOL_MAX_HEIGHT}>
        {groups.length === 0 ? (
          <Box paddingX="sm" paddingY="sm">
            <Text size="sm" variant="muted">
              {`No matches for “${query.trim()}”.`}
            </Text>
          </Box>
        ) : (
          groups.map((group) => (
            <MenuGroup key={group.key}>
              {group.label ? (
                <MenuGroupLabel>{group.label}</MenuGroupLabel>
              ) : null}
              {group.options.map((option) => (
                <MenuCheckboxItem
                  key={option.key}
                  checked={option.selected(value)}
                  onCheckedChange={() => onChange(option.toggle(value))}
                  variant="trailing"
                >
                  <QuickOptionFace option={option} />
                </MenuCheckboxItem>
              ))}
            </MenuGroup>
          ))
        )}
      </Box>
    </>
  );
}
